import React, {
  FunctionComponent,
  SyntheticEvent,
  useEffect,
  useState,
} from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { Redirect } from 'react-router-dom';
import { reject, isEmpty } from 'ramda';
import withStyles from 'react-jss';
import { Grid, Button, Card, Form, Message } from 'semantic-ui-react';
import queryString from 'query-string';

import { ClassProp } from 'helpers/types';

import FormSelect from 'components/FormSelect';

import {
  SearchTypeValues,
  filterTypes,
  Values,
  cardNetworksTypes,
  searchDropDownOptions,
  searchTypes,
  venueTypes,
  VenueTypeValues,
} from './MerchantVenueSearch.helper';

import styles from './MerchantVenueSearch.styles';

type Styles = ClassProp<typeof styles>;

type Props = {
  classes: Styles;
  path: string;
  requireSearch: boolean;
  showFilters: boolean;
  showVMID: boolean;
  isInternalUser?: boolean;
  showOnlyVMID?: boolean;
  isHomeView?: boolean;
};

const buildPathnameBySearchType = (
  searchType: string | undefined,
  path: string,
): string => {
  if (searchType === SearchTypeValues.Vmid) {
    return '/venue-discovery/search';
  }
  if (searchType === SearchTypeValues.MerchantName) {
    return '/';
  }
  return path;
};

const redirectToResults = (values: Values, path: string) => {
  const pathname = buildPathnameBySearchType(values.searchType, path);
  const options = {
    pathname,
    search: queryString.stringify(reject(isEmpty, values)),
  };
  return <Redirect to={options} push />;
};

const renderFilters = (showFilters: boolean, classes: Styles) => {
  if (showFilters) {
    return (
      <>
        <FormSelect
          options={cardNetworksTypes.options}
          placeholder={cardNetworksTypes.placeholder}
          name="cnidsToFilter"
          className={classes.filters}
        />

        <FormSelect
          options={filterTypes.options}
          placeholder={filterTypes.placeholder}
          name="filter"
          className={classes.filters}
        />
      </>
    );
  }
};

const defaultValues = (isHomeView: boolean) => {
  return {
    searchText: '',
    searchType: isHomeView
      ? SearchTypeValues.MerchantName
      : SearchTypeValues.None,
    venueType: VenueTypeValues.All,
    cnidsToFilter: '',
    filter: '',
  };
};

const MerchantVenueSearch: FunctionComponent<Props> = ({
  classes,
  showFilters,
  showVMID,
  path,
  isInternalUser = false,
  showOnlyVMID = false,
  isHomeView = false,
}) => {
  const [isSubmitting, setSubmitting] = useState(false);
  const [currentValues, setCurrentValues] = useState<Values>(
    defaultValues(isHomeView),
  );

  const methods = useForm<Values>({
    mode: 'onBlur',
    defaultValues: defaultValues(isHomeView),
  });

  const {
    handleSubmit,
    errors,
    control,
    reset,
    watch,
    register,
    setValue,
  } = methods;

  const watchValues = watch(['searchText', 'searchType', 'venueType']);

  useEffect(() => {
    const isRequired = isHomeView ? 'Search type may not be blank' : false;
    register('searchType', { required: isRequired });
    register('venueType');

    if (errors) {
      setSubmitting(false);
    }
  }, [errors]);

  const onSubmit = (values: Values) => {
    setCurrentValues(values);
    setSubmitting(true);
    reset();
  };

  const onChange = (_: SyntheticEvent<HTMLElement, Event>, { value }: any) => {
    setValue('searchType', value);
  };

  const venueTypeOnChange = (
    _: SyntheticEvent<HTMLElement, Event>,
    { value }: any,
  ) => {
    setValue('venueType', value);
  };

  if (isSubmitting) {
    return redirectToResults(currentValues, path);
  }

  const isGoButtonDisabled = () => {
    const baseValidation =
      isSubmitting || !watchValues.searchText || !watchValues.searchType;
    return isHomeView ? baseValidation : false;
  };

  return (
    <Grid className={classes.searchGrid} columns="equal">
      <Card fluid className={classes.cardContainer}>
        <Card.Content className={classes.cardContent}>
          <FormProvider {...methods}>
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Form.Input action error={errors.searchText}>
                <Form.Group className={classes.searchGroup}>
                  <Controller
                    name="searchText"
                    control={control}
                    as={
                      <Form.Input
                        icon="search"
                        iconPosition="left"
                        error={errors.searchText}
                        className={classes.searchText}
                        placeholder={
                          showOnlyVMID
                            ? 'Search VMID'
                            : 'Search All Merchant Venues'
                        }
                        name="searchText"
                        type="text"
                      />
                    }
                  />

                  {errors.searchText && (
                    <Message error content={errors.searchText} />
                  )}
                  {!showOnlyVMID && (
                    <FormSelect
                      defaultValue={defaultValues(isHomeView).searchType}
                      options={searchDropDownOptions(
                        showVMID,
                        showOnlyVMID,
                        isInternalUser,
                        isHomeView,
                      )}
                      placeholder={searchTypes.placeholder}
                      name="searchType"
                      onChange={onChange}
                      error={errors.searchType}
                      className={classes.selectSearchBy}
                    />
                  )}
                </Form.Group>

                {showFilters && (
                  <FormSelect
                    defaultValue={defaultValues(isHomeView).venueType}
                    options={venueTypes.options}
                    placeholder={venueTypes.placeholder}
                    name="venueType"
                    onChange={venueTypeOnChange}
                    className={classes.selectSearchBy}
                  />
                )}

                {renderFilters(showFilters, classes)}

                <Form.Field id="form-button-control-public">
                  <Button
                    disabled={isGoButtonDisabled()}
                    type="submit"
                    className={classes.button}
                  >
                    Go
                  </Button>
                </Form.Field>
              </Form.Input>
              {errors.searchText && (
                <Message error content={errors.searchText} />
              )}
            </Form>
          </FormProvider>
        </Card.Content>
      </Card>
    </Grid>
  );
};

export default withStyles(styles)(MerchantVenueSearch);
